import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import RefreshIcon from '@mui/icons-material/Refresh'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import { format } from 'date-fns'
import { guiAgentAPI, healthAPI } from '@services/tanoak-api'

interface AgentStatus {
  id: string
  name: string
  status: 'running' | 'stopped' | 'error'
  lastSync: Date
  nextSync: Date
  dataExtracted: number
  errors: number
  uptime: string
}

interface SystemHealth {
  gui_agent: boolean
  backend_api: boolean
  database: boolean
  tpu_server: boolean
}

const GUIAgentStatus: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [agents, setAgents] = useState<AgentStatus[]>([])
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    gui_agent: true,
    backend_api: true,
    database: true,
    tpu_server: true
  })

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 30000) // Update every 30 seconds
    return () => clearInterval(interval)
  }, [])

  const fetchStatus = async () => {
    try {
      // Fetch agent status and health in parallel
      const [agentData, healthData] = await Promise.all([
        guiAgentAPI.getStatus(),
        healthAPI.getHealth()
      ])

      // Process agent data
      if (agentData?.agents && agentData.agents.length > 0) {
        setAgents(agentData.agents.map((agent: any) => ({
          id: agent.id,
          name: agent.name,
          status: agent.status,
          lastSync: new Date(agent.last_sync),
          nextSync: new Date(agent.next_sync),
          dataExtracted: agent.data_extracted || 0,
          errors: agent.errors || 0,
          uptime: agent.uptime || '99.9%'
        })))
      }

      // Process health data
      if (healthData) {
        setSystemHealth({
          gui_agent: healthData.gui_agent !== false,
          backend_api: healthData.backend_api !== false,
          database: healthData.database !== false,
          tpu_server: healthData.tpu_server !== false
        })
      }
    } catch (error) {
      console.error('Failed to fetch agent status:', error)
      // The API service already provides fallback data
    }
  }

  const handleRefresh = async () => {
    setLoading(true)
    await fetchStatus()
    setLoading(false)
  }

  const handleAgentAction = async (agentId: string, action: 'start' | 'stop' | 'restart') => {
    try {
      await guiAgentAPI.controlAgent(agentId, action)
      await fetchStatus()
    } catch (error) {
      console.error(`Failed to ${action} agent:`, error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'success'
      case 'stopped':
        return 'warning'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          System Status
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* System Health Overview */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  GUI Agent
                </Typography>
                {systemHealth.gui_agent ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </Box>
              <Typography variant="h6">
                {systemHealth.gui_agent ? 'Operational' : 'Error'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Backend API
                </Typography>
                {systemHealth.backend_api ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </Box>
              <Typography variant="h6">
                {systemHealth.backend_api ? 'Operational' : 'Error'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  Database
                </Typography>
                {systemHealth.database ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </Box>
              <Typography variant="h6">
                {systemHealth.database ? 'Connected' : 'Error'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="body2" color="textSecondary">
                  TPU Server
                </Typography>
                {systemHealth.tpu_server ? (
                  <CheckCircleIcon color="success" />
                ) : (
                  <ErrorIcon color="error" />
                )}
              </Box>
              <Typography variant="h6">
                {systemHealth.tpu_server ? 'Active' : 'Offline'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Agent Status Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            GUI Agent Status
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Agent Name</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Last Sync</TableCell>
                  <TableCell>Next Sync</TableCell>
                  <TableCell align="right">Data Extracted</TableCell>
                  <TableCell align="center">Uptime</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id}>
                    <TableCell>{agent.name}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={agent.status}
                        color={getStatusColor(agent.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{format(agent.lastSync, 'pp')}</TableCell>
                    <TableCell>{format(agent.nextSync, 'pp')}</TableCell>
                    <TableCell align="right">
                      ${agent.dataExtracted.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">{agent.uptime}</TableCell>
                    <TableCell align="center">
                      {agent.status === 'running' ? (
                        <IconButton
                          size="small"
                          onClick={() => handleAgentAction(agent.id, 'stop')}
                          title="Stop"
                        >
                          <StopIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleAgentAction(agent.id, 'start')}
                          title="Start"
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleAgentAction(agent.id, 'restart')}
                        title="Restart"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Metrics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Data Extraction Accuracy
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Box flexGrow={1}>
                  <LinearProgress variant="determinate" value={98} />
                </Box>
                <Typography variant="body2">98%</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                System Uptime (30 days)
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Box flexGrow={1}>
                  <LinearProgress variant="determinate" value={99.9} />
                </Box>
                <Typography variant="body2">99.9%</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mt: 2 }} />}

      {/* Alert for any issues */}
      {agents.some(a => a.status === 'error') && (
        <Alert severity="error" sx={{ mt: 2 }}>
          One or more agents are experiencing errors. Please check the logs for details.
        </Alert>
      )}
    </Box>
  )
}

export default GUIAgentStatus